import { styled, Icon } from '@moleculeone/ui';

import { ReagentTag } from './ReagentTag';
import { COMPOUND_NAME_FROM_SMILES } from './compounds';
import { ReferenceReaction } from './YieldReferences';

export type YieldRange = {
  name: 'low' | 'medium' | 'high';
  predicted_yield: string;
  probability_mean: number;
};

export type PredictionData = {
  predictions: YieldRange[];
  certainty: number;
  reference_reactions: ReferenceReaction[];
  reagents: Record<string, string>;
};

const PredictionContainer = styled('button', {
  'all': 'unset',
  'display': 'block',
  'height': 'max-content',
  'padding': '$3 $5 0',
  'userSelect': 'none',
  'cursor': 'pointer',
  'position': 'relative',
  'borderBottom': '1px solid $slate5',
  'background': '$slate2',
  '&:hover': {
    background: '$slate4',
  },
  'variants': {
    active: {
      true: {
        background: '$slate4',
        position: 'relative',
      },
    },
  },
});

const PredictionReagents = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '$3',
});

const Yields = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$2',
  marginBottom: '$2',
});

const Yield = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$2',
  fontSize: '$1',
});

const YieldName = styled('span', {
  width: '4rem',
  textAlign: 'right',
  color: '$slate11',
});

const YieldProbability = styled('div', {
  flex: 1,
});

const YieldBar = styled('div', {
  borderRadius: '$small',
  background: '$slate4',
  color: '$slate1',
  textAlign: 'center',
  variants: {
    type: {
      low: {
        background: '$indigo9',
      },
      medium: {
        background: '$purple9',
      },
      high: {
        background: '$blue9',
      },
    },
  },
});

const Certainty = styled('div', {
  marginLeft: '4rem',
  display: 'flex',
  alignItems: 'center',
  gap: '$2',
  fontSize: '$1',
  color: '$slate11',
  [`& ${Icon}`]: {
    fontSize: '$3',
  },
});

const OpenPrediction = styled('div', {
  textAlign: 'center',
});

const CertaintyType = styled('span', {
  variants: {
    type: {
      high: {
        color: '$green10',
      },
      medium: {
        color: '$amber10',
      },
      low: {
        color: '$red10',
      },
    },
  },
});

interface YieldPredictionProps extends React.ComponentPropsWithoutRef<'button'> {
  prediction: YieldRange[];
  certainty: number;
  reagents: Record<string, string>;
  active: boolean;
  onClick: () => void;
}

function getCertainty(certainty: number) {
  if (certainty >= 0.5) {
    return 'high';
  }
  if (certainty >= 0.1) {
    return 'medium';
  }
  return 'low';
}

export const YieldPrediction = ({
  prediction,
  certainty,
  reagents,
  onClick,
  ...restProps
}: YieldPredictionProps) => {
  const certaintyType = getCertainty(certainty);
  const maxProbability = Math.max(...prediction.map(({ probability_mean }) => probability_mean));

  return (
    <PredictionContainer onClick={onClick} {...restProps}>
      <PredictionReagents>
        {Object.entries(reagents).map(([key, value]) => {
          const compoundName = COMPOUND_NAME_FROM_SMILES[value];
          return (
            <ReagentTag
              key={key}
              condition={key}
              text={compoundName ?? value}
              smiles={compoundName && value}
              fullWidth
            />
          );
        })}
      </PredictionReagents>
      <Yields>
        {prediction.map(({ name, predicted_yield, probability_mean }) => (
          <Yield key={name}>
            <YieldName>{`${predicted_yield}% yield`}</YieldName>
            <YieldProbability>
              <YieldBar
                type={name}
                css={{
                  width: `${Math.max(1, (probability_mean / maxProbability) * 100)}%`,
                }}
              >
                {`${Math.round(probability_mean * 100)}%`}
              </YieldBar>
            </YieldProbability>
          </Yield>
        ))}
      </Yields>
      <Certainty>
        <Icon icon="neurology" />{' '}
        <CertaintyType type={certaintyType}>{certaintyType}</CertaintyType> certainty
      </Certainty>

      <OpenPrediction>
        <Icon icon="arrow_drop_down" />
      </OpenPrediction>
    </PredictionContainer>
  );
};
